import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { catchError, finalize, map, of, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { Popover } from 'primeng/popover';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';

import { endOfDay, format } from '@core/date.facade';
import { U2bDatePickerButtonBarComponent } from '@u2bUi/date-picker/u2b-date-picker-button-bar.component';
import { U2B_DATE_PICKER_CUSTOM_TIME_PT } from '@u2bUi/date-picker/u2b-date-picker.pt';
import { U2bDatePickerApplyDirective } from '@u2bUi/directives/u2b-date-picker-apply.directive';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationModule } from '@core/translation/translation.module';
import { ElectricMeterApiService } from '@bcmApiServices/electric-meter.api-service';
import { ElectricMeterCabinetApiService } from '@bcmApiServices/electric-meter-cabinet.api-service';
import { ElectricMeterProductTypeApiService } from '@bcmApiServices/electric-meter-product-types.api-service';
import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { filterElectricMeterProducts } from '@shared/constants/electric-meter-product-category';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { findDefaultProduct } from '@modules/bcm/electric-meters/readings/reading-dialog/reading-dialog-product-defaults';
import { BcmSettings } from '@shared/models/bcm-settings';
import { ElectricMeter } from '@shared/models/electric-meter';
import { ElectricMeterCabinet } from '@shared/models/electric-meter-cabinet';
import { ElectricMeterProductKey, ElectricMeterProductType } from '@shared/models/electric-meter-product-type';
import { IReading, Reading } from '@shared/models/electric-meter-reading';
import { ReadingsService } from '@modules/bcm/electric-meters/readings/readings.service';
import { Product } from '@shared/models/product';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bIndicatorCardComponent } from '@u2bUi/indicator-card/u2b-indicator-card.component';

import {
    BookingV2ElectricityProductOption,
    BookingV2ElectricMeterAdditionalFee,
    BookingV2ElectricMeterAssignment,
    BookingV2ElectricMeterCabinetOption,
    BookingV2ElectricMeterOption,
    BookingV2ElectricMeterProductTypeOption,
} from '../../../dtos/booking-v2.dtos';
import { BookingV2Facade } from '../../../services/booking-v2.facade';
import { formatQuantity } from '../../../utils/booking-v2-pricing';
import {
    BookingV2ElectricMeterReadingDialogComponent,
    BookingV2EMReadingMode,
    BookingV2EMReadingResult,
} from './electric-meter-reading-dialog.component';

@Component({
    selector: 'booking-v2-electric-meters-tab',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DatePipe,
        DecimalPipe,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        Checkbox,
        DatePicker,
        InputNumber,
        Popover,
        ProgressSpinner,
        Select,
        Tooltip,
        TranslationModule,
        U2bDateMaskDirective,
        U2bDatePickerApplyDirective,
        U2bDatePickerButtonBarComponent,
        U2bIconButtonComponent,
        U2bIndicatorCardComponent,
        BookingV2ElectricMeterReadingDialogComponent,
    ],
    templateUrl: './electric-meters-tab.component.html',
    host: { class: 'block' },
})
export class ElectricMetersTabComponent {

    readonly readOnly = input<boolean>(false);

    private readonly facade = inject(BookingV2Facade);
    private readonly cabinetApi = inject(ElectricMeterCabinetApiService);
    private readonly meterApi = inject(ElectricMeterApiService);
    private readonly productTypeApi = inject(ElectricMeterProductTypeApiService);
    private readonly productsApi = inject(ProductsApiService);
    private readonly settingsFacade = inject(BcmSettingsFacade);
    private readonly notify = inject(AppNotificationService);
    private readonly readingsService = inject(ReadingsService);
    private readonly tenantService = inject(BcmTenantService);

    // PrimeNG inputnumber locale for German decimals (defaults to de-DE).
    readonly currentLocale = BcmService.currentLocale;

    fmtQty = formatQuantity;

    // DualUse-Kennzeichen (#7250) nur mit Modulrecht `dual_use` anzeigen.
    readonly showDualUse = this.tenantService.checkPermission(BcmTenantPermission.DUAL_USE);

    readonly assignments = this.facade.electricMeterAssignments;

    // ── Per-assignment actions (edit mode) ──
    // The active action dialog (intermediate reading / disconnect / edit start),
    // null when closed. `liveReading` seeds the dialog for web-switchable meters.
    readonly activeAction = signal<{
        mode: BookingV2EMReadingMode;
        assignment: BookingV2ElectricMeterAssignment;
        liveReading: number | null;
    } | null>(null);

    // Live readings for the assignment cards (web-switchable meters, keyed by uuid).
    readonly cardLiveReadings = signal<Record<string, number | null>>({});
    private readonly liveFetched = new Set<string>();

    readonly customTimePt = U2B_DATE_PICKER_CUSTOM_TIME_PT;
    readonly today = endOfDay(new Date());

    readonly berthName = computed(() => {
        const berth = this.facade.selectedBerths()[0];
        if (!berth) {
            return null;
        }
        return berth.pierHandle ? `${berth.pierHandle} · ${berth.handle}` : berth.handle;
    });

    // ── Reference data (real backend) ──
    readonly loadingCabinets = signal<boolean>(true);

    readonly cabinets = toSignal(
        this.cabinetApi.getAll().pipe(
            map(list => list.filter(c => c.electricMeters.length > 0).map(toCabinetOption)),
            catchError(() => of<BookingV2ElectricMeterCabinetOption[]>([])),
            finalize(() => this.loadingCabinets.set(false)),
        ),
        { initialValue: [] as BookingV2ElectricMeterCabinetOption[] },
    );

    readonly productTypes = toSignal(
        this.productTypeApi.getAll().pipe(
            map(list => list.map(toProductTypeOption)),
            catchError(() => of<BookingV2ElectricMeterProductTypeOption[]>([])),
        ),
        { initialValue: [] as BookingV2ElectricMeterProductTypeOption[] },
    );

    // Full product catalog — feeds the (filtered) dropdown and the default lookup
    // (the configured default may live in the full list, not just the EM category).
    readonly products = toSignal(
        this.productsApi.getAll().pipe(
            catchError(() => of<Product[]>([])),
        ),
        { initialValue: [] as Product[] },
    );

    // Electricity products (system category id 2, uniqueId fallback) for the select.
    readonly electricityProducts = computed<BookingV2ElectricityProductOption[]>(() =>
        filterElectricMeterProducts(this.products()).map(toProductOption),
    );

    // Tenant settings — hold the per-type default electricity products.
    readonly settings = toSignal(
        this.settingsFacade.loadSettings().pipe(
            catchError(() => of<BcmSettings | undefined>(undefined)),
        ),
        { initialValue: undefined as BcmSettings | undefined },
    );

    // ── Add form ──
    readonly showForm = signal<boolean>(false);
    readonly berthId = signal<number | null>(null);
    readonly cabinetId = signal<number | null>(null);
    readonly meterId = signal<number | null>(null);
    readonly from = signal<Date | null>(null);
    readonly meterReadingStart = signal<number | null>(null);
    readonly productTypeId = signal<number | null>(null);
    readonly productId = signal<number | null>(null);
    readonly quantity = signal<number | null>(null);

    // Optional flat base fee (Grundgebühr) for by-usage meters.
    readonly additionalFeeEnabled = signal<boolean>(false);
    readonly additionalProductId = signal<number | null>(null);
    readonly additionalQuantity = signal<number | null>(null);

    // Live reading for web-switchable (API) meters — read from the provider and
    // shown read-only in place of a manual start-reading entry.
    readonly liveReading = signal<number | null>(null);
    readonly liveReadingLoading = signal<boolean>(false);

    readonly loadingMeters = signal<boolean>(false);

    // Billing type drives which fields apply: NO_CHARGE → no product/quantity;
    // BY_USAGE → product but no quantity (consumption-based); else → both.
    readonly selectedProductTypeKey = computed(() =>
        this.productTypes().find(p => p.id === this.productTypeId())?.key ?? null,
    );

    readonly showProduct = computed(() => {
        const key = this.selectedProductTypeKey();
        return key !== null && key !== ElectricMeterProductKey.NO_CHARGE;
    });

    readonly showQuantity = computed(() =>
        this.showProduct() && this.selectedProductTypeKey() !== ElectricMeterProductKey.BY_USAGE,
    );

    // The flat base fee (Grundgebühr) is only offered for by-usage billing.
    readonly showAdditionalFee = computed(() => this.selectedProductTypeKey() === ElectricMeterProductKey.BY_USAGE);

    readonly additionalFeeActive = computed(() => this.showAdditionalFee() && this.additionalFeeEnabled());

    // Meters are loaded per cabinet; the connection date is sent as a snapshot
    // at load time (mirrors the legacy dialog, which reloads only on cabinet
    // change). Editing the date afterwards must NOT reload or clear the
    // selection — that was the cause of the cabinet dropping its selection.
    readonly meters = toSignal(
        toObservable(this.cabinetId).pipe(
            switchMap(cabinetId => {
                if (cabinetId == null) {
                    this.loadingMeters.set(false);
                    return of<BookingV2ElectricMeterOption[]>([]);
                }
                this.loadingMeters.set(true);
                let params = new HttpParams().set('bcm_electric_meter_cabinets_id', String(cabinetId));
                const from = this.from();
                if (from) {
                    params = params.set('from', format(from, 'yyyy-MM-dd'));
                }
                return this.meterApi.getAll(params).pipe(
                    map(list => list.map(toMeterOption)),
                    catchError(() => of<BookingV2ElectricMeterOption[]>([])),
                    finalize(() => this.loadingMeters.set(false)),
                );
            }),
        ),
        { initialValue: [] as BookingV2ElectricMeterOption[] },
    );

    // Meters not already assigned in this booking (prevents double-assignment).
    readonly availableMeters = computed(() =>
        this.meters().filter(m => !this.isMeterAssigned(m.id)),
    );

    readonly selectedMeter = computed(() => this.meters().find(m => m.id === this.meterId()) ?? null);

    readonly selectedCabinet = computed(() => this.cabinets().find(c => c.id === this.cabinetId()) ?? null);

    // Berth-occupancy options (only offered as a choice when the booking spans
    // several berths; otherwise the single berth is used implicitly).
    readonly berthOptions = computed(() =>
        this.facade.selectedBerths().map(b => ({
            id: b.id,
            label: b.pierHandle ? `${b.pierHandle} · ${b.handle}` : b.handle,
        })),
    );
    readonly showBerthSelect = computed(() => this.facade.selectedBerths().length > 1);

    // Web-switchable (CampConcept/ZFA) selection: the reading is read live from
    // the provider, so the manual start-reading entry is hidden and not required.
    readonly isApiMeter = computed(() => this.selectedCabinet()?.switchableViaWeb === true);

    // Connection date may not be in the future (recomputes "now" on each read, so
    // a long-open dialog or a typed/masked date can't slip a future date through).
    readonly fromIsFuture = computed(() => {
        const from = this.from();
        if (!from) {
            return false;
        }
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return from.getTime() > endOfToday.getTime();
    });

    readonly canSave = computed(() =>
        this.cabinetId() !== null
        && this.meterId() !== null
        && this.from() !== null
        && !this.fromIsFuture()
        // A meter with an unfinished reading can't be attached (backend rejects it).
        && !this.selectedMeter()?.hasOpenReading
        // API meters: the start reading is read live by the backend, not typed.
        && (this.isApiMeter() || this.meterReadingStart() !== null)
        && this.productTypeId() !== null
        && (!this.showProduct() || this.productId() !== null)
        && (!this.showQuantity() || this.quantity() !== null)
        && (!this.additionalFeeActive() || this.additionalProductId() !== null),
    );

    constructor() {
        // Mark the advanced step dirty while the add form is open & unsaved, so
        // navigating away (tab/step/dialog) prompts to discard.
        effect(() => this.facade.advancedDraftDirty.set(!this.readOnly() && this.showForm()));

        // Re-apply the tenant default once settings/products finish loading, in case
        // a billing type was chosen before those async streams emitted. Reads
        // productId untracked so it never clobbers a value the user set/cleared.
        effect(() => {
            const def = this.defaultProductFor(this.productTypeId());
            if (this.showForm() && def !== null && untracked(this.productId) === null) {
                this.productId.set(def);
            }
        });

        // Fetch the live reading for existing web-switchable assignments that are
        // still open (no end reading) — mirrors the legacy card's ngOnInit.
        effect(() => {
            for (const a of this.assignments()) {
                const apiMeter = a.provider?.switchableViaWeb === true;
                if (apiMeter && a.assignmentId != null && a.meterReadingEnd == null && !this.liveFetched.has(a.uuid)) {
                    this.liveFetched.add(a.uuid);
                    this.meterApi.getCurrentReading(a.meterId).subscribe({
                        next: r => this.cardLiveReadings.update(m => ({ ...m, [a.uuid]: r.reading })),
                        error: () => { /* live value unavailable — card just omits it */ },
                    });
                }
            }
        });
    }

    // Physical-only providers can't be web-switched/read, so interim reading and
    // disconnect are unavailable (mirrors the legacy `providerNoWebSwitch` gate).
    isPhysicalOnly(a: BookingV2ElectricMeterAssignment): boolean {
        return !!(a.provider && !a.provider.switchableViaWeb);
    }

    toggleForm(): void {
        if (this.showForm()) {
            this.closeForm();
        } else {
            // The connection moment is now, not the harbour check-in on the booking.
            this.from.set(new Date());
            this.berthId.set(this.facade.selectedBerths()[0]?.id ?? null);
            this.showForm.set(true);
        }
    }

    closeForm(): void {
        this.showForm.set(false);
        this.berthId.set(this.facade.selectedBerths()[0]?.id ?? null);
        this.cabinetId.set(null);
        this.meterId.set(null);
        this.from.set(new Date());
        this.meterReadingStart.set(null);
        this.productTypeId.set(null);
        this.productId.set(null);
        this.quantity.set(null);
        this.additionalFeeEnabled.set(false);
        this.additionalProductId.set(null);
        this.additionalQuantity.set(null);
        this.liveReading.set(null);
        this.liveReadingLoading.set(false);
    }

    onProductTypeChange(id: number | null): void {
        this.productTypeId.set(id);
        // Reset billing fields, then pre-fill the tenant default product for
        // chargeable types (matches the legacy dialog).
        this.quantity.set(null);
        this.additionalFeeEnabled.set(false);
        this.additionalProductId.set(null);
        this.additionalQuantity.set(null);
        this.productId.set(this.defaultProductFor(id));
    }

    // Tenant default product for a billing type (per productType.settingsKey), but
    // only when it is actually offered by the dropdown — otherwise the select would
    // show blank while a hidden id passes validation.
    private defaultProductFor(typeId: number | null): number | null {
        const type = this.productTypes().find(p => p.id === typeId) ?? null;
        if (!type || type.key === ElectricMeterProductKey.NO_CHARGE) {
            return null;
        }
        const def = findDefaultProduct(this.settings(), type as ElectricMeterProductType, this.products());
        return def && this.electricityProducts().some(p => p.id === def.id) ? def.id : null;
    }

    onCabinetChange(id: number | null): void {
        this.cabinetId.set(id);
        this.meterId.set(null);
        this.meterReadingStart.set(null);
        this.liveReading.set(null);
        this.liveReadingLoading.set(false);
    }

    onFromChange(date: Date | null): void {
        // Date is just a snapshot for the assignment; it neither reloads the
        // meter list nor clears the cabinet/meter selection.
        this.from.set(date);
    }

    onMeterChange(id: number | null): void {
        this.meterId.set(id);
        if (this.isApiMeter()) {
            // Provider-controlled: no manual reading; read the live value instead.
            this.meterReadingStart.set(null);
            this.fetchLiveReading(id);
        } else {
            const meter = this.meters().find(m => m.id === id) ?? null;
            this.meterReadingStart.set(meter ? meter.lastReading : null);
            this.liveReading.set(null);
        }
    }

    private fetchLiveReading(meterId: number | null): void {
        this.liveReading.set(null);
        if (meterId == null) {
            return;
        }
        this.liveReadingLoading.set(true);
        this.meterApi.getCurrentReading(meterId).pipe(
            finalize(() => this.liveReadingLoading.set(false)),
        ).subscribe({
            next: r => this.liveReading.set(r.reading),
            error: () => this.liveReading.set(null),
        });
    }

    save(): void {
        if (!this.canSave()) {
            return;
        }
        const cabinet = this.cabinets().find(c => c.id === this.cabinetId());
        const meter = this.selectedMeter();
        const productType = this.productTypes().find(p => p.id === this.productTypeId());
        if (!cabinet || !meter || !productType) {
            return;
        }
        // Surface the conflict here instead of letting the booking save fail server-side.
        if (meter.hasOpenReading) {
            this.notify.showError(TolgeeService.translate('bookingV2.advanced.power.openReadingBlocked'));
            return;
        }
        const product = this.showProduct()
            ? this.electricityProducts().find(p => p.id === this.productId()) ?? null
            : null;
        const apiMeter = this.isApiMeter();
        const additionalFee = this.buildAdditionalFee(cabinet, meter);
        const assignment: BookingV2ElectricMeterAssignment = {
            uuid: uuidv4(),
            cabinetId: cabinet.id,
            cabinetHandle: cabinet.handle,
            meterId: meter.id,
            meterHandle: meter.handle,
            meterFullName: meter.fullName,
            from: this.from(),
            // API meters: the backend reads + uses the live value on connect; the
            // fetched live reading is stored only for display (ignored server-side).
            meterReadingStart: apiMeter ? (this.liveReading() ?? 0) : (this.meterReadingStart() as number),
            meterReadingEnd: null,
            to: null,
            berthId: this.berthId(),
            berthLabel: this.berthOptions().find(b => b.id === this.berthId())?.label ?? null,
            productTypeId: productType.id,
            productTypeKey: productType.key,
            productTypeLabel: productType.description,
            productId: product?.id ?? null,
            productName: product?.name ?? null,
            quantity: this.showQuantity() ? this.quantity() : null,
            productPrice: product?.price ?? null,
            productVatValue: product?.vatValue ?? null,
            additionalFee,
            provider: cabinet.switchableViaWeb === null ? null : { switchableViaWeb: cabinet.switchableViaWeb },
            hasDualUse: meter.hasDualUse,
        };
        this.facade.electricMeterAssignments.update(list => [...list, assignment]);
        this.closeForm();
    }

    private buildAdditionalFee(
        cabinet: BookingV2ElectricMeterCabinetOption,
        meter: BookingV2ElectricMeterOption,
    ): BookingV2ElectricMeterAdditionalFee | null {
        if (!this.additionalFeeActive()) {
            return null;
        }
        const product = this.electricityProducts().find(p => p.id === this.additionalProductId());
        if (!product) {
            return null;
        }
        const base = TolgeeService.translate('bookingV2.advanced.power.additionalFeeTitle');
        return {
            productId: product.id,
            title: `${base} · ${cabinet.handle} · ${meter.handle}`,
            price: product.price,
            quantity: this.additionalQuantity() ?? 1,
            unitId: product.unitId,
            taxRateId: product.taxRateId,
        };
    }

    remove(uuid: string): void {
        const assignment = this.assignments().find(a => a.uuid === uuid);
        if (!assignment) {
            return;
        }

        if (assignment.assignmentId == null) {
            this.facade.electricMeterAssignments.update(list => list.filter(a => a.uuid !== uuid));
            return;
        }

        if (assignment.readingId == null) {
            this.facade.removeElectricMeterFromEditState(assignment.assignmentId);
            return;
        }

        const reading = { id: assignment.readingId } as IReading;
        const meter = { id: assignment.meterId } as ElectricMeter;

        this.readingsService.confirmAndDeleteReading(reading, 'full', meter).subscribe({
            next: () => {
                this.facade.removeElectricMeterFromEditState(assignment.assignmentId as number);
                this.notify.showSuccess(TolgeeService.translate('bcmElectricMeters.readings.removed'));
            },
            error: () => this.notify.showError(TolgeeService.translate('bcmElectricMeters.readings.removeFailed')),
        });
    }

    isMeterAssigned(meterId: number): boolean {
        return this.assignments().some(a => a.meterId === meterId);
    }

    // ── Card display helpers (legacy-dialog parity) ──

    consumption(a: BookingV2ElectricMeterAssignment): number | null {
        return a.meterReadingEnd !== null ? a.meterReadingEnd - a.meterReadingStart : null;
    }

    liveConsumption(a: BookingV2ElectricMeterAssignment): number | null {
        const live = this.cardLiveReadings()[a.uuid];
        return live != null && a.meterReadingStart != null ? live - a.meterReadingStart : null;
    }

    // ── Per-assignment actions (edit mode) ──

    startAction(mode: BookingV2EMReadingMode, a: BookingV2ElectricMeterAssignment): void {
        this.activeAction.set({ mode, assignment: a, liveReading: this.cardLiveReadings()[a.uuid] ?? null });
        // Refresh the live value for web-switchable meters before reading out.
        if (mode !== 'editStart' && a.provider?.switchableViaWeb === true) {
            this.meterApi.getCurrentReading(a.meterId).subscribe({
                next: r => this.activeAction.update(s => (s && s.assignment.uuid === a.uuid ? { ...s, liveReading: r.reading } : s)),
                error: () => { /* dialog falls back to server-side read on disconnect */ },
            });
        }
    }

    onActionVisibleChange(visible: boolean): void {
        if (!visible) {
            this.activeAction.set(null);
        }
    }

    onActionConfirmed(result: BookingV2EMReadingResult): void {
        const act = this.activeAction();
        if (!act) {
            return;
        }
        const a = act.assignment;
        if (result.mode === 'editStart') {
            this.applyEditStart(a, result);
        } else if (result.mode === 'intermediate') {
            this.applyIntermediate(a, result);
        } else {
            this.applyDisconnect(a, result);
        }
    }

    // Edit start reading. For a persisted reading this is saved immediately
    // (existing readings are otherwise left untouched by the commit); for a
    // meter still being added this just edits the local draft.
    private applyEditStart(a: BookingV2ElectricMeterAssignment, result: BookingV2EMReadingResult): void {
        const { startDate, startMeterReading } = result;
        if (startDate == null || startMeterReading == null) {
            return;
        }
        if (a.readingId == null) {
            this.updateAssignment(a.uuid, { from: startDate, meterReadingStart: startMeterReading });
            return;
        }
        this.meterApi.updateStartReading(a.meterId, a.readingId, { startDate, startMeterReading }).subscribe({
            next: () => {
                this.updateAssignment(a.uuid, { from: startDate, meterReadingStart: startMeterReading });
                this.notify.showSuccess(TolgeeService.translate('bookingV2.advanced.power.action.editStartSaved'));
            },
            error: () => this.notify.showError(TolgeeService.translate('bookingV2.advanced.power.action.saveFailed')),
        });
    }

    // Interim reading: persisted immediately via the meter API (as in the legacy
    // dialog); the new open reading id + end value are kept for display.
    private applyIntermediate(a: BookingV2ElectricMeterAssignment, result: BookingV2EMReadingResult): void {
        if (a.assignmentId == null || result.endDate == null) {
            return;
        }
        this.meterApi.intermediateReading(a.meterId, a.assignmentId, {
            endDate: result.endDate,
            endMeterReading: result.endMeterReading ?? undefined,
            productId: result.productId ?? undefined,
        }).subscribe({
            next: res => {
                this.updateAssignment(a.uuid, { meterReadingEnd: res.endMeterReading, readingId: res.newReadingId });
                this.notify.showSuccess(TolgeeService.translate('bookingV2.advanced.power.action.intermediateSaved'));
            },
            error: () => this.notify.showError(TolgeeService.translate('bookingV2.advanced.power.action.saveFailed')),
        });
    }

    // Disconnect (Trennen): staged locally, applied by the commit through the
    // backend disconnect path (close-out + power-off for web-switchable meters).
    private applyDisconnect(a: BookingV2ElectricMeterAssignment, result: BookingV2EMReadingResult): void {
        this.updateAssignment(a.uuid, {
            disconnected: true,
            to: result.endDate ?? null,
            meterReadingEnd: result.endMeterReading ?? null,
            productId: result.productId ?? a.productId,
        });
    }

    undoDisconnect(uuid: string): void {
        this.updateAssignment(uuid, { disconnected: false, to: null, meterReadingEnd: null });
    }

    private updateAssignment(uuid: string, patch: Partial<BookingV2ElectricMeterAssignment>): void {
        this.facade.electricMeterAssignments.update(list =>
            list.map(x => (x.uuid === uuid ? { ...x, ...patch } : x)),
        );
    }
}

function toCabinetOption(c: ElectricMeterCabinet): BookingV2ElectricMeterCabinetOption {
    const pierHandle = c.pierHandle || null;
    const switchableViaWeb = c.provider ? !!c.provider.switchableViaWeb : null;
    return {
        id: c.id,
        handle: c.handle,
        pierHandle,
        label: pierHandle ? `${c.handle} · ${pierHandle}` : c.handle,
        meterCount: c.electricMeters.length,
        switchableViaWeb,
        // Physical-only providers can't be switched/read here → not selectable.
        disabled: switchableViaWeb === false,
    };
}

function toMeterOption(m: ElectricMeter): BookingV2ElectricMeterOption {
    const occupied = (m.boats?.length ?? 0) > 0;
    const hasOpenReading = (m.readings || [])
        .some(r => r.startMeterReading != null && r.endMeterReading == null);
    return {
        id: m.id,
        handle: m.handle,
        fullName: m.fullName,
        cabinetId: m.cabinet?.id ?? null,
        fuse: m.fuse || null,
        lastReading: lastReadingOf(m.readings),
        occupied,
        hasOpenReading,
        disabled: occupied || hasOpenReading,
        hasDualUse: !!m.hasDualUse,
    };
}

function toProductTypeOption(p: ElectricMeterProductType): BookingV2ElectricMeterProductTypeOption {
    return { id: p.id, key: p.key, settingsKey: p.settingsKey, description: p.description };
}

function toProductOption(p: Product): BookingV2ElectricityProductOption {
    return {
        id: p.id,
        name: p.name,
        price: p.price,
        label: `${p.name} · ${p.price.toFixed(2)} €`,
        taxRateId: p.taxRate?.id ?? null,
        unitId: p.unit?.id ?? null,
        vatValue: p.taxRate?.value ?? 0,
    };
}

// Highest reading on record — used as the default and the lower bound for the
// new assignment's start reading (a meter never runs backwards).
function lastReadingOf(readings: readonly Reading[]): number {
    const vals = (readings || [])
        .flatMap(r => [r.startMeterReading, r.endMeterReading])
        .filter((v): v is number => typeof v === 'number' && !isNaN(v));
    return vals.length ? Math.max(...vals) : 0;
}
